// Erkennt schreibgeschützte (verschlüsselte) PDFs, BEVOR ein Signatur-Vorgang entsteht.
//
// Vorfall 2026-08-13: Ein Wohnraummietvertrag ließ sich hochladen, mit Feldern versehen und
// von beiden Seiten unterschreiben — erst das Finalisieren scheiterte, dreimal hintereinander
// („Ihre Signatur wurde gespeichert, die Fertigstellung ist aber fehlgeschlagen"). Ursache war
// nicht der zweite Unterzeichner, sondern die Datei: AES-128-verschlüsselt (/V 4/R 4, /CFM/AESV2).
//
// Warum es so spät auffiel: Der Editor zeigt das PDF über pdfjs an, und pdfjs KANN geschützte
// Dateien lesen. Die Edge Function brennt die Unterschriften dagegen mit pdf-lib ein, und pdf-lib
// kann verschlüsselte PDFs weder parsen noch schreiben — auch `ignoreEncryption` hilft nicht
// (dann bricht das Laden an den unlesbaren Objekten ab). Der Fehler MUSS deshalb beim Hochladen
// auffallen, nicht am Ende: Sonst haben alle Beteiligten bereits unterschrieben, wenn es scheitert.
//
// Erkennung: Ein verschlüsseltes PDF trägt `/Encrypt` im Trailer bzw. im Cross-Reference-Stream.
// Beides steht am Dateiende, bei linearisierten PDFs zusätzlich am Anfang. Wir sehen deshalb
// gezielt in diesen beiden Bereichen nach und NICHT in der ganzen Datei — mitten im Dokument
// kann dieselbe Zeichenfolge zufällig in einem Datenstrom stehen, und ein Fehlalarm würde ein
// gültiges Dokument grundlos abweisen.

/** Wie viel vom Anfang und vom Ende betrachtet wird — großzügig genug für Trailer und XRef-Stream. */
const RANDBEREICH = 64 * 1024;

const MARKE = '/Encrypt';

function enthaeltMarke(bytes: Uint8Array, von: number, bis: number): boolean {
  const ende = Math.min(bis, bytes.length);
  if (von >= ende) return false;
  let text = '';
  for (let i = von; i < ende; i++) text += String.fromCharCode(bytes[i]);
  return text.includes(MARKE);
}

/**
 * Ist dieses PDF verschlüsselt und damit für das Einbrennen von Unterschriften unbrauchbar?
 * Prüft Datei-Anfang und -Ende; der Rest bleibt bewusst außen vor (s. Kommentar oben).
 */
export function istPdfVerschluesselt(bytes: Uint8Array): boolean {
  if (bytes.length === 0) return false;
  if (enthaeltMarke(bytes, 0, RANDBEREICH)) return true;
  return enthaeltMarke(bytes, Math.max(0, bytes.length - RANDBEREICH), bytes.length);
}

/** Meldung für die Oberfläche — nennt das Problem und den Weg heraus. */
export const PDF_GESCHUETZT_MELDUNG =
  'Dieses PDF ist schreibgeschützt und lässt sich deshalb nicht unterschreiben. '
  + 'Bitte die Datei ohne Schutz speichern (z. B. über „Drucken → Als PDF sichern") und erneut hochladen.';
